import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { motion, useReducedMotion } from "framer-motion";
import { Loader2, Search, Undo2 } from "lucide-react";
import { api, formatApiError } from "../../../api";
import { DESK } from "../../../constants/testIds";
import { formatCurrency } from "../../../lib/format";
import { fadeUp } from "../../../lib/motion";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../../components/ui/alert-dialog";
import { StatusPill } from "../../../components/common/StatusPill";
import { toast } from "../../../components/ui/sonner";
import { useCelebration } from "../../../contexts/CelebrationContext";

// UX-FD-03 §1 SCR-P02-83 + highest-risk rule #4 (type-to-confirm on high-stakes actions):
// `payment.refund` already sits ONLY on the Manager-tier role on this backend (front_of_house_fd
// has no such permission at all) — this screen IS the "above the FD limit" approval surface, not
// a separate escalation on top of an FD action. Only a REAL payment (a real `payment_cash`/
// `payment_digital` ledger credit) can be refunded — there is no "waiver" concept anywhere on
// this backend (no write-off-a-due endpoint exists independent of a real prior payment), so that
// half of the prompt's screen name is a documented, flagged gap rather than a fabricated button.
export default function RefundApproval() {
  const queryClient = useQueryClient();
  const reducedMotion = useReducedMotion();
  const { celebrate } = useCelebration();
  const [query, setQuery] = useState("");
  const [submitted, setSubmitted] = useState("");
  const [member, setMember] = useState(null);
  const [target, setTarget] = useState(null);

  const searchQ = useQuery({
    queryKey: ["desk-mgr-refund-search", submitted],
    queryFn: async () => {
      const isPhone = /^\+?\d[\d\s-]{5,}$/.test(submitted);
      const params = isPhone ? { phone: submitted.replace(/[\s-]/g, "") } : { name: submitted };
      return (await api.get("/members", { params: { ...params, limit: 10 } })).data.members;
    },
    enabled: !!submitted,
  });

  const ledgerQ = useQuery({
    queryKey: ["desk-mgr-refund-ledger", member?.id],
    queryFn: async () => (await api.get("/ledger", { params: { member_ref: member.id } })).data.entries,
    enabled: !!member,
  });

  const payments = (ledgerQ.data || []).filter((e) => ["payment_cash", "payment_digital"].includes(e.type) && e.direction === "credit");
  const refundedPaymentIds = new Set((ledgerQ.data || []).filter((e) => e.type === "refund").map((e) => e.related_ref));

  const refundMutation = useMutation({
    mutationFn: ({ paymentRef, amount, reason }) => api.post(
      "/refunds", { payment_ref: paymentRef, amount, reason, to: "rail" },
      { headers: { "Idempotency-Key": crypto.randomUUID() } },
    ),
    onSuccess: () => {
      celebrate("refund_processed");
      setTarget(null);
      queryClient.invalidateQueries({ queryKey: ["desk-mgr-refund-ledger"] });
    },
    onError: (e) => toast.error(formatApiError(e).message),
  });

  return (
    <div data-testid={DESK.mgrRefundPage} className="space-y-6 max-w-2xl">
      <motion.div {...fadeUp(reducedMotion)}>
        <h1 className="text-2xl font-bold" style={{ color: "#141416" }}>Refund approval</h1>
        <p className="text-sm mt-0.5" style={{ color: "#68686E" }}>Find a member, then refund a real past payment — reason required, type-to-confirm.</p>
      </motion.div>

      <motion.form {...fadeUp(reducedMotion, 0.04)} onSubmit={(e) => { e.preventDefault(); setMember(null); setSubmitted(query.trim()); }} className="flex gap-2">
        <Input data-testid={DESK.mgrRefundSearchInput} placeholder="Member name or phone…" value={query} onChange={(e) => setQuery(e.target.value)} />
        <Button type="submit" data-testid={DESK.mgrRefundSearchButton} disabled={!query.trim()} className="text-white gap-2" style={{ background: "#141416" }}><Search size={15} /> Find</Button>
      </motion.form>

      {searchQ.isLoading && <p className="text-sm flex items-center gap-2" style={{ color: "#68686E" }}><Loader2 size={14} className="animate-spin" /> Searching…</p>}

      {!member && searchQ.data?.map((m) => (
        <button key={m.id} data-testid={DESK.mgrRefundMemberRow(m.id)} onClick={() => setMember(m)} className="w-full flex items-center justify-between rounded-[12px] px-4 py-3 text-left scarlet-focusable" style={{ background: "#fff", border: "1px solid #EBEAE5" }}>
          <div>
            <p className="font-semibold text-sm" style={{ color: "#141416" }}>{m.profile.name}</p>
            <p className="text-xs" style={{ color: "#68686E" }}>{m.profile.phone}</p>
          </div>
        </button>
      ))}

      {member && (
        <motion.div {...fadeUp(reducedMotion)} className="rounded-[14px] overflow-hidden" style={{ background: "#fff", border: "1px solid #EBEAE5" }}>
          <p className="px-4 py-3 text-sm font-semibold" style={{ color: "#141416", borderBottom: "1px solid #EBEAE5" }}>{member.profile.name}'s payments</p>
          {payments.length === 0 && <p className="p-4 text-sm" style={{ color: "#68686E" }}>No refundable payments found for this member.</p>}
          {payments.map((p) => {
            const alreadyRefunded = refundedPaymentIds.has(p.related_ref);
            return (
              <div key={p._id} data-testid={DESK.mgrRefundEntryRow(p._id)} className="flex items-center justify-between px-4 py-3" style={{ borderBottom: "1px solid #F4F3EF" }}>
                <div>
                  <p className="text-sm font-medium" style={{ color: "#141416" }}>{formatCurrency(p.amount)} · {p.type === "payment_cash" ? "Cash" : "Digital"}</p>
                  <p className="text-xs" style={{ color: "#68686E" }}>{new Date(p.at).toLocaleString()}</p>
                </div>
                {alreadyRefunded ? (
                  <StatusPill status="overdue" label="Refunded" />
                ) : (
                  <Button size="sm" variant="outline" data-testid={DESK.mgrRefundButton(p._id)} onClick={() => setTarget(p)} className="gap-1"><Undo2 size={13} /> Refund</Button>
                )}
              </div>
            );
          })}
        </motion.div>
      )}

      <RefundConfirmDialog target={target} onClose={() => setTarget(null)} memberName={member?.profile?.name} onConfirm={(payload) => refundMutation.mutate(payload)} busy={refundMutation.isPending} />
    </div>
  );
}

function RefundConfirmDialog({ target, onClose, memberName, onConfirm, busy }) {
  const [reason, setReason] = useState("");
  const [amountRupees, setAmountRupees] = useState("");
  const [confirmText, setConfirmText] = useState("");

  if (!target) return null;
  const maxAmount = target.amount / 100;
  const amount = Math.round(Number(amountRupees || maxAmount) * 100);
  const valid = reason.trim() && confirmText === memberName && amount > 0 && amount <= target.amount;

  return (
    <AlertDialog open={!!target} onOpenChange={(v) => { if (!v) { onClose(); setReason(""); setAmountRupees(""); setConfirmText(""); } }}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Refund {formatCurrency(target.amount)} to {memberName}?</AlertDialogTitle>
          <AlertDialogDescription>
            Original payment on {new Date(target.at).toLocaleString()}. Type <strong>{memberName}</strong> to confirm.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="space-y-2">
          <Input data-testid={DESK.mgrRefundAmountInput} type="number" min={1} max={maxAmount} placeholder={`Amount (₹, up to ${maxAmount})`} value={amountRupees} onChange={(e) => setAmountRupees(e.target.value)} />
          <Input data-testid={DESK.mgrRefundReasonInput} placeholder="Reason for refund (required)" value={reason} onChange={(e) => setReason(e.target.value)} />
          <Input data-testid={DESK.mgrRefundConfirmInput} placeholder={memberName} value={confirmText} onChange={(e) => setConfirmText(e.target.value)} />
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            data-testid={DESK.mgrRefundConfirmSubmit}
            disabled={!valid || busy}
            onClick={() => onConfirm({ paymentRef: target.related_ref, amount, reason })}
            className="text-white" style={{ background: "var(--scarlet-neg)" }}
          >
            Confirm refund
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
